import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { KeyRound, Mail, ArrowRight, RefreshCw, LogIn } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [timer]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, { email });
      setStep(2);
      setTimer(60);
      toast.success('Verification code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, otp });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      if (response.data.login_log_id) {
        localStorage.setItem('login_log_id', response.data.login_log_id);
      }
      onLogin(response.data.user);
      toast.success('Login successful');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resetFlow = () => {
    setStep(1);
    setOtp('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#fafaf9] p-4 font-sans">
      {/* Background Decorative Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-[#022c22]/10 blur-3xl"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#d4af37]/10 blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl border border-stone-200 shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-[#022c22] p-8 text-center relative overflow-hidden">
             <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none bg-[radial-gradient(circle_at_center,_white_0%,_transparent_70%)]"></div>
             <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center mx-auto mb-4 border border-white/20 shadow-inner">
               <span className="text-[#d4af37] font-serif text-3xl font-bold">R</span>
             </div>
             <h1 className="text-2xl font-serif font-bold text-white mb-2">Rudraksh 925</h1>
             <p className="text-[#d4af37]/80 text-xs font-medium uppercase tracking-[0.2em]">Jewellery ERP System</p>
          </div>

          <div className="p-8 pb-10">
             <div className="mb-8 text-center">
                <h2 className="text-xl font-bold text-[#022c22] mb-1">
                  {step === 1 ? 'Welcome Back' : 'Verify Identity'}
                </h2>
                <p className="text-sm text-stone-500">
                  {step === 1 
                    ? 'Enter your registered email to continue' 
                    : `We've sent a 6-digit code to ${email}`}
                </p>
             </div>

             {step === 1 ? (
               <form onSubmit={handleRequestOTP} className="space-y-5">
                 <div className="space-y-2">
                   <Label htmlFor="email" className="text-stone-600 text-xs font-bold uppercase tracking-wider">Email Address</Label>
                   <div className="relative group">
                     <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-[#022c22] transition-colors" size={18} />
                     <Input
                        id="email"
                        type="email"
                        placeholder="admin@rudraksh925.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-10 h-12 bg-stone-50 border-stone-200 focus:border-[#022c22] focus:ring-1 focus:ring-[#022c22]/20 transition-all"
                     />
                   </div>
                 </div>

                 <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#022c22] text-[#d4af37] hover:bg-[#064e3b] font-bold text-sm shadow-lg hover:shadow-[#022c22]/20 transition-all flex items-center justify-center gap-2 group"
                 >
                    {loading ? <RefreshCw className="animate-spin" size={20} /> : (
                      <>
                        <span>Send Login Code</span>
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                 </Button>
               </form>
             ) : (
               <form onSubmit={handleLogin} className="space-y-6">
                 <div className="space-y-2">
                   <Label htmlFor="otp" className="text-stone-600 text-xs font-bold uppercase tracking-wider">Verification Code</Label>
                   <div className="relative group">
                     <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-[#022c22] transition-colors" size={18} />
                     <Input
                        id="otp"
                        type="text"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                        required
                        autoFocus
                        className="pl-10 h-12 bg-stone-50 border-stone-200 focus:border-[#022c22] focus:ring-1 focus:ring-[#022c22]/20 text-center text-xl font-bold tracking-[0.5em]"
                     />
                   </div>
                 </div>

                 <div className="space-y-4">
                   <Button
                      type="submit"
                      disabled={loading || otp.length < 6}
                      className="w-full h-12 rounded-xl bg-[#022c22] text-[#d4af37] hover:bg-[#064e3b] font-bold text-sm shadow-lg hover:shadow-[#022c22]/20 transition-all flex items-center justify-center gap-2"
                   >
                      {loading ? <RefreshCw className="animate-spin" size={20} /> : (
                        <>
                          <LogIn size={18} />
                          <span>Verify & Access System</span>
                        </>
                      )}
                   </Button>

                   <div className="flex flex-col items-center gap-3">
                      <button 
                        type="button"
                        onClick={handleRequestOTP}
                        disabled={loading || timer > 0}
                        className={`text-sm font-medium transition-colors ${timer > 0 ? 'text-stone-400 cursor-not-allowed' : 'text-[#022c22] hover:underline'}`}
                      >
                        {timer > 0 ? `Resend code in ${timer}s` : 'Did not receive a code? Resend'}
                      </button>
                      
                      <button 
                        type="button"
                        onClick={resetFlow}
                        className="text-xs text-stone-400 hover:text-stone-600 transition-colors uppercase tracking-widest"
                      >
                        Change Email
                      </button>
                   </div>
                 </div>
               </form>
             )}
          </div>
        </div>
        
        <p className="mt-8 text-center text-xs text-stone-400">
          Secure Access Protocol &bull; Rudraksh 925 ERP &bull; v2.0
        </p>
      </div>
    </div>
  );
};

export default Login;